import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from "react";

const SIZER_STYLES: React.CSSProperties = {
  position: "absolute",
  width: 0,
  height: 0,
  visibility: "hidden",
  overflow: "scroll",
  whiteSpace: "pre",
};

const STYLE_PROPS = [
  "fontSize",
  "fontFamily",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
] as const;

export interface TagsSelectInputProps {
  autoResize?: boolean;
  autoFocus?: boolean;
  query?: string;
  expandable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  listboxId?: string;
  selectedIndex?: number;
  type?: React.HTMLInputTypeAttribute;
}

export interface TagsSelectInputHandle {
  focus: () => void;
}

const TagsSelectInput: ForwardRefRenderFunction<
  TagsSelectInputHandle,
  TagsSelectInputProps
> = (
  {
    autoResize,
    autoFocus,
    query,
    expandable,
    placeholder,
    disabled,
    listboxId,
    selectedIndex,
    type,
  },
  ref
) => {
  const [inputWidth, setInputWidth] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const sizer = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      input.current?.focus();
    },
  }));

  const copyInputStyles = () => {
    if (!input.current || !sizer.current) {
      return;
    }
    const inputStyle = window.getComputedStyle(input.current);
    STYLE_PROPS.forEach((prop) => {
      sizer.current!.style[prop] = inputStyle[prop];
    });
  };

  useEffect(() => {
    let inputWidth: number | undefined;
    if (autoResize) {
      // scrollWidth is designed to be fast not accurate.
      // +2 is completely arbitrary but does the job.
      inputWidth = Math.ceil(sizer.current!.scrollWidth) + 2;
    }
    // Preserved from JS: `inputWidth !== inputWidth` is always false,
    // so setInputWidth is never called here. Behavior kept intact.
    if (inputWidth !== inputWidth) {
      setInputWidth(inputWidth ?? null);
    }
  }, [autoResize, setInputWidth]);

  useEffect(() => {
    if (autoResize) {
      copyInputStyles();
    }

    // Preserved from JS: `input.value` reads the ref object's (absent)
    // `value`, not `input.current.value`. Behavior kept intact.
    if ((input as { value?: string }).value !== query) {
      if (input.current) {
        input.current.value = query ?? "";
      }
    }

    if (autoFocus) {
      input.current?.focus();
    }
  }, [autoFocus, autoResize, query]);

  const sizerText = query || placeholder;

  const selectedId = `${listboxId}-${selectedIndex}`;

  return (
    <div
      className={`tags-select__search-input ${disabled ? "is-disabled" : ""}`}
      data-testid={"tags-search-input"}
    >
      <input
        ref={input}
        role="combobox"
        aria-autocomplete="list"
        aria-label={placeholder}
        aria-owns={listboxId}
        aria-activedescendant={
          selectedIndex !== undefined && selectedIndex > -1
            ? selectedId
            : undefined
        }
        aria-expanded={expandable}
        disabled={disabled}
        placeholder={placeholder}
        style={{ width: inputWidth ?? undefined }}
        type={type}
      />
      <div ref={sizer} style={SIZER_STYLES}>
        {sizerText}
      </div>
    </div>
  );
};

export default forwardRef(TagsSelectInput);
